import {
  Component,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  EventEmitter,
} from "@angular/core";
import { VAIconRegistryService, infoCircle } from "@va/core-library/icons";
import { AppService } from "src/app/app.service";

export enum USE_FOR {
  SOURCE_SELECTION,
  CC_SELECTION,
  EXPORT_TO_EXCEL_CC_SELECTION,
}

@Component({
  selector: "app-source-table",
  templateUrl: "./source-table.component.html",
  styleUrls: ["./source-table.component.scss"],
  encapsulation: ViewEncapsulation.None,
  host: { class: "source-table" },
})
export class SourceTableComponent implements OnChanges {
  UseFor = USE_FOR;
  @Input() brokersList = [];
  @Input() showOnlyCurrent = false;
  @Input() sortDirection = null;
  @Input() sortColumn = null;
  @Input() useFor;
  @Input() ccState = false;
  @Input() currentActiveRadio;
  @Input() showColumns = { name: true, analyst: true, modeldate: true };
  @Output() selectionChanged: EventEmitter<{selection: number[]}> = new EventEmitter(); // prettier-ignore
  @Output() toggleBrokerListRadio: EventEmitter<any> = new EventEmitter(); // prettier-ignore
  @Output() showCurrentChanged: EventEmitter<{showOnlyCurrent: boolean}> = new EventEmitter(); // prettier-ignore
  @Output() sortChanged: EventEmitter<{sortColumn: string;sortDirection: string}> = new EventEmitter(); // prettier-ignore

  //cache variables. write to these avoid modifying @Input variables
  localShowOnlyCurrent = false;
  localSortDirection = null;
  localSortColumn = null;

  brokersListCopy = [];
  selectedBrokers = [];
  tableYScroll = 182;

  sortKeys: { [key: string]: string | null } = {
    modeldatetime: null,
    name: null,
  };
  constructor(
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([infoCircle]);
  }
  ngOnChanges(changes: SimpleChanges) {
    if (!changes.brokersList.currentValue) {
      return;
    }

    this.localShowOnlyCurrent = this.showOnlyCurrent;
    this.localSortColumn = this.sortColumn;
    this.localSortDirection = this.sortDirection;

    this.brokersListCopy = this.appService.superClone(this.brokersList);
    this.onSortChange(this.localSortColumn, this.localSortDirection);
    if (this.currentActiveRadio !== "reaffirm") {
      this.selectedBrokers = this.brokersListCopy
        .filter((b) =>
          this.useFor === USE_FOR.SOURCE_SELECTION ? b.selected : b.selected_cc,
        )
        .map((b) => b.sourceid);
    }
    this.onShowCurrentSwitch(this.localShowOnlyCurrent);
  }

  /**
   * fired on toggle of show only current checkbox
   * @param switchedOn boolean state of checkbox
   */
  onShowCurrentSwitch = (switchedOn: boolean) => {
    this.localShowOnlyCurrent = switchedOn;
    this.brokersListCopy = this.appService.superClone(this.brokersList);
    this.onSortChange(this.localSortColumn, this.localSortDirection);

    if (switchedOn) {
      //first remove any excluded broker from selected sources
      //because it may be possible that an exluded broker was selected before switch turn on
      const excludedBrokers = this.brokersListCopy.filter((b) => b.excluded);

      excludedBrokers.forEach((broker) => {
        //perform uncheck operation (remove from selected list)
        this.onRowChecked(broker.sourceid, false);
      });

      //now filter brokerlist to discard excluded brokers
      this.brokersListCopy = this.brokersListCopy.filter((b) => !b.excluded);
    }

    //emit the current value out
    this.showCurrentChanged.emit({ showOnlyCurrent: switchedOn });
  };

  /**
   * fired on column sort change
   * @param param0 { key: "modeldatetime" | "name", value: "ascend" | "descend" | null }
   */
  onSortChange(key: string, value: string) {
    this.localSortColumn = key;
    this.localSortDirection = value;

    //bring broker list copy to initial state
    // this.brokersListCopy = this.appService.superClone(this.brokersListCopy);

    //allow sort only one column at a time
    this.sortKeys = { modeldatetime: null, name: null };
    this.sortKeys[key] = value;

    if (value === null) {
      //no sorting order applied -> then return orginal sort state
      //brokerList already in original sort state
      if (this.localShowOnlyCurrent) {
        this.brokersListCopy = this.brokersList.filter((b) => !b.excluded);
      } else {
        this.brokersListCopy = this.appService.superClone(this.brokersList);
      }
      return;
    }

    const reverse = "descend".includes(value) ? true : false;
    const keyOnObject = key === "name" ? "name" : "modeldatetime";
    if (keyOnObject === "name") {
      this.brokersListCopy = [
        ...this.appService.basicSort(
          this.brokersListCopy,
          [keyOnObject],
          reverse,
          (str: string) => {
            return str.toLowerCase();
          },
        ),
      ];
    } else {
      this.brokersListCopy = [
        ...this.appService.basicSort(
          this.brokersListCopy,
          [keyOnObject],
          reverse,
        ),
      ];
    }

    //emit the current value out
    this.sortChanged.emit({
      sortColumn: this.localSortColumn,
      sortDirection: this.localSortDirection,
    });
  }

  /**
   * fired when checkbox corresponding to a source is toggled,
   * even if as a side effect of selectAll or showOnlyCurrent
   * @param sourceid integer source id of broker
   * @param isChecked boolean selection state
   */
  onRowChecked = (sourceid: number, isChecked: boolean) => {
    if (this.selectedBrokers.includes(sourceid) && !isChecked) {
      //remove this source
      this.selectedBrokers = this.selectedBrokers.filter(
        (source) => source !== sourceid,
      );
    }

    if (!this.selectedBrokers.includes(sourceid) && isChecked) {
      //add source id to slected sources
      this.selectedBrokers.push(sourceid);
      //sorting is necessary to avoid unnecessary diffs when comparing
      this.selectedBrokers = this.appService.basicSort(this.selectedBrokers);
    }

    //emit the current value out
    this.selectionChanged.emit({ selection: this.selectedBrokers });
    this.toggleBrokerListRadio.emit();
  };

  /**
   * fired when check all checkbox from table header is toggled
   * @param isChecked boolean state
   */
  onCheckAll(isChecked: boolean) {
    let selectedSources = []; //deselect all

    if (isChecked) {
      selectedSources = this.selectedBrokers;
      //check all the brokers that are currently visible
      this.brokersListCopy.forEach((broker) => {
        if (!selectedSources.includes(broker.sourceid) && isChecked) {
          //add source id to slected sources
          selectedSources.push(broker.sourceid);
        }
      });
    }

    //sorting is necessary to avoid unnecessary diffs when comparing
    this.selectedBrokers = this.appService.basicSort(selectedSources);

    //emit the current value out
    this.selectionChanged.emit({ selection: this.selectedBrokers });
    this.toggleBrokerListRadio.emit();
  }
}
